import { snakeCase } from "es-toolkit";
import { useMemo } from "react";
import type { TranslatedString } from "@web-monorepo/i18next";
import { useEnsureFeatureSwitches, useFetchedFeatureSwitchesWithOverrides } from "@web-monorepo/feature-switches";
import { DevToolsSettings } from "./devToolsSettings";
import { IconChevronDown, IconUndo } from "@web-monorepo/dds-icons";
import {
  DDSBodyText,
  DDSButton,
  DDSDropdownMenu,
  DDSDropdownMenuContent,
  DDSDropdownMenuItem,
  DDSDropdownMenuTrigger,
  DDSLabelText,
  DDSMicroText,
  DDSTextField,
} from "@web-monorepo/dds";
import styles from "./FeatureSwitchesSection.module.css";
import { getPredefinedValuesForSwitch } from "./featureSwitchOptions";
import { useFeatureSwitchesSectionVM, type HoldoutData } from "./FeatureSwitchesSection.vm";

interface FeatureSwitchConfigShape<T extends string> {
  name: T;
  value: string;
  predefinedValues: string[];
  isOverridden: boolean;
}

export const FeatureSwitchesSectionContainer = ({ windowObject = window }: { windowObject?: typeof window }) => {
  const featureSwitchesValues = useFetchedFeatureSwitchesWithOverrides<string>();
  const { holdoutData } = useEnsureFeatureSwitches();

  const {
    featureSwitchOverrides,
    setFeatureSwitchOverride,
    clearFeatureSwitchOverride,
    clearAllFeatureSwitchOverrides,
  } = DevToolsSettings.useFeatureSwitchOverrides();

  const featureSwitches = useMemo(
    () =>
      Object.entries(featureSwitchesValues).map(([fsName, value]) => ({
        name: fsName,
        value,
        predefinedValues: getPredefinedValuesForSwitch(fsName, value),
        isOverridden: Object.prototype.hasOwnProperty.call(featureSwitchOverrides, fsName),
      })),
    [featureSwitchOverrides, featureSwitchesValues],
  );

  return (
    <FeatureSwitchesSection
      holdoutData={holdoutData}
      featureSwitches={featureSwitches}
      overrideCount={Object.keys(featureSwitchOverrides).length}
      onChange={setFeatureSwitchOverride}
      resetFeatureSwitch={clearFeatureSwitchOverride}
      resetAllFeatureSwitches={clearAllFeatureSwitchOverrides}
      windowObject={windowObject || window}
    />
  );
};

const FeatureSwitchesSectionHeader = ({ overrideCount }: { overrideCount: number }) => {
  return overrideCount === 0 ? (
    <DDSLabelText>Feature Switches</DDSLabelText>
  ) : (
    <DDSLabelText>
      Feature Switches{" "}
      <DDSBodyText variant="danger" level="sm">
        ({overrideCount} override{overrideCount === 1 ? "" : "s"})
      </DDSBodyText>
    </DDSLabelText>
  );
};

const FeatureSwitchesSection = ({
  featureSwitches,
  overrideCount,
  onChange,
  resetFeatureSwitch,
  resetAllFeatureSwitches,
  windowObject = window,
  holdoutData,
}: FeatureSwitchesSectionProps) => {
  const vm = useFeatureSwitchesSectionVM({
    featureSwitches: featureSwitches ?? [],
    holdoutData,
  });

  return (
    <div className={styles.root}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionHeaderTitle}>
          <FeatureSwitchesSectionHeader overrideCount={overrideCount ?? 0} />
        </div>
        <DDSButton
          variant="unstyled"
          onClick={resetAllFeatureSwitches}
          data-name="dev_tools_reset_all_feature_switches"
        >
          <DDSMicroText className={styles.resetAllButton}>Reset switches to defaults</DDSMicroText>
        </DDSButton>
      </div>
      <div className={styles.searchInput}>
        <DDSTextField
          data-name="dev_tools_feature_switches_search"
          placeholder="Search feature switches..."
          size="small"
          ariaLabel="Search feature switches"
          value={vm.search}
          onChange={(value) => {
            vm.setSearch(value);
          }}
        />
      </div>
      <div className={styles.scrollArea}>
        {vm.holdoutWarnings.length > 0 && (
          <div className={styles.warningBanner} disable-tag-translation>
            <DDSLabelText variant="warning">Errors</DDSLabelText>
            {vm.holdoutWarnings.map((warning) => (
              <DDSBodyText key={warning} variant="warning" level="sm">
                {warning as TranslatedString}
              </DDSBodyText>
            ))}
          </div>
        )}
        {vm.overriddenFeatureSwitches.length > 0 && (
          <div className={styles.overridesSection}>
            <DDSLabelText variant="danger" className={styles.overridesSectionTitle}>
              Overrides
            </DDSLabelText>
            <div className={styles.featureSwitchesList}>
              {vm.overriddenFeatureSwitches.map((fs) => {
                return (
                  <FeatureSwitchItem
                    key={fs.name}
                    name={fs.name}
                    value={fs.value}
                    predefinedValues={fs.predefinedValues}
                    isOverridden={fs.isOverridden}
                    onChange={onChange}
                    windowObject={windowObject}
                    resetFeatureSwitch={resetFeatureSwitch}
                  />
                );
              })}
            </div>
          </div>
        )}
        {vm.filteredFeatureSwitches && (
          <div className={styles.featureSwitchesList}>
            {vm.filteredFeatureSwitches.map((fs) => {
              return (
                <FeatureSwitchItem
                  key={fs.name}
                  name={fs.name}
                  value={fs.value}
                  predefinedValues={fs.predefinedValues}
                  isOverridden={fs.isOverridden}
                  onChange={onChange}
                  windowObject={windowObject}
                  resetFeatureSwitch={resetFeatureSwitch}
                />
              );
            })}
          </div>
        )}
        {vm.holdoutFeatureSwitches && Object.keys(vm.holdoutFeatureSwitches).length > 0 && (
          <div className={styles.holdoutSection}>
            <DDSLabelText className={styles.holdoutSectionTitle}>Holdout Feature Switches</DDSLabelText>
            {Object.entries(vm.holdoutFeatureSwitches).map(([holdoutName, group]) => {
              return (
                <div key={holdoutName} className={styles.holdoutGroup}>
                  <div className={styles.featureSwitchesList}>
                    <FeatureSwitchItem
                      key={group.value.name}
                      name={group.value.name}
                      value={group.value.value}
                      predefinedValues={group.value.predefinedValues}
                      isOverridden={group.value.isOverridden}
                      onChange={onChange}
                      windowObject={windowObject}
                      resetFeatureSwitch={resetFeatureSwitch}
                    />
                    <div className={styles.holdoutSwitchesList}>
                      {group.fs.map((fs) => {
                        return (
                          <FeatureSwitchItem
                            key={fs.name}
                            name={fs.name}
                            value={fs.value}
                            predefinedValues={fs.predefinedValues}
                            isOverridden={fs.isOverridden}
                            onChange={onChange}
                            windowObject={windowObject}
                            resetFeatureSwitch={resetFeatureSwitch}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
interface FeatureSwitchesSectionProps {
  featureSwitches?: FeatureSwitchConfigShape<string>[];
  overrideCount?: number;
  onChange: (name: string, value: string) => void;
  resetFeatureSwitch: (name: string) => void;
  resetAllFeatureSwitches: () => void;
  holdoutData: HoldoutData | undefined;
  windowObject?: typeof window;
}

const FeatureSwitchItem = ({
  name,
  value,
  predefinedValues,
  isOverridden = false,
  onChange,
  windowObject,
  resetFeatureSwitch,
}: FeatureSwitchItemProps) => {
  const menuItems = [
    ...(predefinedValues || []).map((val) => ({
      value: val,
      onClick: () => onChange(name, val),
      "data-name": snakeCase(val),
    })),
    {
      value: "Custom...",
      onClick: () => {
        const newValue = windowObject.prompt(`Please enter new value for [${name}]`);
        if (newValue) {
          onChange(name, newValue);
        }
      },
      "data-name": "custom",
    },
  ];

  return (
    <div className={styles.featureSwitchItem}>
      <div className={styles.featureSwitchRow}>
        {/* Only marketplace enforces its prefix via TypeScript (`web_marketplace_${string}`),
           so we can safely strip it as noise. Other apps use varied prefixes (web_parent_,
           web_school_leader_, etc.) that may be meaningful. Expand this when an app adds
           similar type-level guarantees for its prefix. */}
        <div className={styles.featureSwitchName}>{name.replace(/^web_marketplace_/, "")}</div>
        <div className={styles.featureSwitchControls}>
          <DDSDropdownMenu>
            <DDSDropdownMenuTrigger asChild>
              <DDSButton
                variant="unstyled"
                data-name={`feature_switches_section:feature_switch_dropdown_menu:${name}`}
                className={styles.dropdownTrigger}
              >
                <span
                  className={
                    isOverridden
                      ? styles.featureSwitchValueOverridden
                      : value === "off"
                        ? styles.featureSwitchValueOff
                        : styles.featureSwitchValueOn
                  }
                >
                  {value as TranslatedString}
                </span>
                <IconChevronDown size="xs" />
              </DDSButton>
            </DDSDropdownMenuTrigger>
            <DDSDropdownMenuContent>
              {menuItems.map((item) => (
                <DDSDropdownMenuItem
                  key={item.value}
                  data-name={`feature_switches_section:feature_switch_dropdown_menu:${name}:${item["data-name"]}`}
                  onClick={item.onClick}
                >
                  {item.value}
                </DDSDropdownMenuItem>
              ))}
            </DDSDropdownMenuContent>
          </DDSDropdownMenu>
          <div className={styles.resetButtonWrapper}>
            <DDSButton
              variant="unstyled"
              data-name="feature_switches_section:reset_feature_switch"
              onClick={() => resetFeatureSwitch(name)}
              aria-label={`Reset ${name} to default` as TranslatedString}
            >
              <IconUndo size="s" />
            </DDSButton>
          </div>
        </div>
      </div>
    </div>
  );
};
interface FeatureSwitchItemProps {
  name: string;
  value: string;
  predefinedValues: string[];
  isOverridden?: boolean;
  onChange: (name: string, value: string) => void;
  windowObject: typeof window;
  resetFeatureSwitch: (name: string) => void;
}
